"use client";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { onboardingSupport as data } from "@/lib/data/support";

const accent = "var(--color-emerald)";
const phaseAccents = ["var(--color-cyan)", "var(--color-purple)", "var(--color-amber)", "var(--color-emerald)", "var(--color-crimson)"];

export function OnboardingSupport() {
  return (
    <Section id="onboarding-support" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Phase</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Duration</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden lg:table-cell">Activities</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Deliverable</th>
                  </tr>
                </thead>
                <tbody>
                  {data.phases.map((p, i) => {
                    const c = phaseAccents[i % phaseAccents.length]!;
                    return (
                      <tr key={p.phase} className="border-b border-[rgb(var(--border)/0.08)]">
                        <td className="px-4 py-3 align-top font-semibold" style={{ color: c }}>{p.phase}</td>
                        <td className="px-4 py-3 align-top hidden md:table-cell text-[rgb(var(--fg-muted))] font-mono text-xs">{p.duration}</td>
                        <td className="px-4 py-3 align-top hidden lg:table-cell text-[rgb(var(--fg-muted))]">{p.activities}</td>
                        <td className="px-4 py-3 align-top font-medium">{p.deliverable}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <div className="mt-8 grid lg:grid-cols-2 gap-5">
          <Reveal delay={200}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Onboarding Resources</span>
              <ul className="mt-3 space-y-1.5">
                {data.resources.map((r, i) => (
                  <li key={i} className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={250}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>{data.transition.title}</span>
              <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{data.transition.body}</p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
